import { create } from "zustand"
import { persist, createJSONStorage } from "zustand/middleware"
import type { QuizAnswers } from "@/features/quiz/types"
import { setOnboardingQuizId } from "@/features/onboarding-lock"

interface QuizState {
  currentStepIndex: number
  answers: QuizAnswers
  completed: boolean
  /** Registry key of the quiz currently in progress (for switch detection). */
  activeQuizId: string | null
  setStepIndex: (index: number) => void
  setAnswer: (stepId: string, value: string | string[]) => void
  /** Activate a quiz; resets progress only when switching to a different one. */
  startQuiz: (id: string) => void
  complete: () => void
  reset: () => void
}

export const useQuizStore = create<QuizState>()(
  persist(
    (set) => ({
      currentStepIndex: 0,
      answers: {},
      completed: false,
      activeQuizId: null,
      setStepIndex: (index) => set({ currentStepIndex: index }),
      setAnswer: (stepId, value) => set((s) => ({ answers: { ...s.answers, [stepId]: value } })),
      startQuiz: (id) => {
        // Mirror into localStorage whenever the funnel quiz is (re)activated so
        // webapp onboarding-lock can resolve the quiz after sessionStorage is gone.
        setOnboardingQuizId(id)
        set((s) =>
          s.activeQuizId === id
            ? {}
            : { activeQuizId: id, currentStepIndex: 0, answers: {}, completed: false },
        )
      },
      complete: () => set({ completed: true }),
      reset: () => set({ currentStepIndex: 0, answers: {}, completed: false }),
    }),
    {
      name: "bp-quiz",
      storage: createJSONStorage(() => sessionStorage),
    },
  ),
)
